/**
 * PsychometricOnboarding — optional 20-trait paid-tier baseline.
 *
 * Layers on top of the mandatory Investment Strategy Gatekeeper. Once
 * completed, Obondo's coaching engine uses the composite score + weakest
 * group to time behavioural nudges during trading sessions.
 *
 * Not a gatekeeper — users can trade without completing it.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, BrainCircuit, CheckCircle2, Lock } from "lucide-react";
import { t } from "@/lib/i18n-toasts";
import ReadingLevelToggle from "@/components/ReadingLevelToggle";
import QuestionnaireLanguageToggle from "@/components/QuestionnaireLanguageToggle";

const LIKERT_TICKS = [1, 2, 3, 4, 5];

// Draft answers persist in localStorage so:
//   1. Users can resume a half-finished questionnaire without losing progress.
//   2. PsychometricBaselineNudge can render a radial progress ring reflecting
//      how many of the 20 traits the user has already answered.
export const PSYCHOMETRIC_DRAFT_KEY = "md_psychometric_draft";
export const PSYCHOMETRIC_TOTAL = 20;

// Localised "draft saved" hint for the baseline auto-save toast.
const DRAFT_SAVED_HINT = {
    EN: "Draft saved · you can resume any time",
    SW: "Rasimu imehifadhiwa · endelea wakati wowote",
    SH: "Draft saved · resume wakati wowote",
    FR: "Brouillon enregistré · reprenez à tout moment",
    AR: "تم حفظ المسودة · يمكنك المتابعة في أي وقت",
    PT: "Rascunho salvo · retome quando quiser",
};

export default function PsychometricOnboarding() {
    const navigate = useNavigate();
    const { user, refresh } = useAuth();
    const lang = user?.preferred_language;
    const [level, setLevel] = useState(user?.reading_level || "basic");
    const [language, setLanguage] = useState((user?.preferred_language || "en").toLowerCase());
    const [data, setData] = useState(null);
    // Hydrate answers from localStorage so returning users pick up where
    // they left off. Wrapped in try/catch — a bad JSON blob just resets.
    const [answers, setAnswers] = useState(() => {
        try {
            const raw = localStorage.getItem(PSYCHOMETRIC_DRAFT_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === "object") return parsed;
            }
        } catch { /* ignore */ }
        return {};
    });
    const [step, setStep] = useState(0);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [paywalled, setPaywalled] = useState(false);

    useEffect(() => {
        api.get(`/psychometric/questions?level=${level}&language=${language}`)
            .then(({ data }) => setData(data))
            .catch((e) => {
                if (e?.response?.status === 402) setPaywalled(true);
                else toast.error(t("onboarding_load_failed", lang));
            });
    }, [lang, level, language]);

    // Persist draft answers on every change so the nudge chip on the dashboard
    // can render a live radial progress ring.
    useEffect(() => {
        try {
            if (Object.keys(answers).length > 0) {
                localStorage.setItem(PSYCHOMETRIC_DRAFT_KEY, JSON.stringify(answers));
            }
        } catch { /* quota/private mode → silent */ }
    }, [answers]);

    const traits = data?.traits || [];
    const total = traits.length;
    const answeredCount = useMemo(() => Object.keys(answers).length, [answers]);
    const q = step < total ? traits[step] : null;

    if (paywalled) {
        return (
            <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="psychometric-paywall">
                <div className="flex items-center gap-2 text-primary">
                    <Lock className="w-4 h-4" /> Paid tier feature
                </div>
                <h1 className="text-2xl font-bold">Psychometric Baseline</h1>
                <p className="text-sm text-muted-foreground">
                    The 20-trait behavioural profile is available on Advanced Trader
                    and Institutional plans. It powers Obondo's live bias-detection
                    coaching — patience, overconfidence, sunk-cost, adherence, and 16
                    other traits are scored so Obondo can nudge you at exactly the
                    moment a bias is about to bite.
                </p>
                <Button onClick={() => navigate("/app/subscription")} data-testid="psychometric-upgrade">
                    See paid plans
                </Button>
            </div>
        );
    }

    if (!data) {
        return <div className="p-8 text-sm text-muted-foreground">Loading baseline…</div>;
    }

    if (result) {
        return (
            <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="psychometric-done">
                <div className="flex items-center gap-2 text-emerald-500">
                    <CheckCircle2 className="w-5 h-5" />
                    <h1 className="text-2xl font-bold">Baseline saved</h1>
                </div>
                <div className="rounded-lg border border-border bg-card p-4">
                    <div className="text-xs uppercase text-muted-foreground">Composite score</div>
                    <div className="text-3xl font-bold" data-testid="psychometric-composite">
                        {result.composite_score} / 100
                    </div>
                    <div className="text-sm text-primary uppercase font-semibold mt-1">
                        {result.behavioural_bucket.replace("_", " ")}
                    </div>
                    <p className="text-sm mt-3">{result.summary}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(result.group_scores || {}).map(([k, g]) => (
                        <div key={k} className="rounded border border-border p-3 text-sm">
                            <div className="text-xs uppercase text-muted-foreground">{g.label}</div>
                            <div className="font-bold text-lg">{g.average} / 5</div>
                        </div>
                    ))}
                </div>
                <Button onClick={() => { refresh?.(); navigate("/app"); }} data-testid="psychometric-continue">
                    Continue to dashboard
                </Button>
            </div>
        );
    }

    const canNext = q ? Boolean(answers[q.id]) : answeredCount === total;

    async function submit() {
        setBusy(true);
        try {
            const { data } = await api.post("/psychometric/submit", { answers, level });
            setResult(data);
            toast.success(`Baseline saved — ${data.behavioural_bucket.replace("_", " ")}`);
            // Baseline recorded server-side → clear the draft so the nudge
            // stops showing progress for an already-completed run.
            try { localStorage.removeItem(PSYCHOMETRIC_DRAFT_KEY); } catch { /* ignore */ }
            await refresh?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || t("onboarding_submit_failed", lang));
        } finally { setBusy(false); }
    }

    return (
        <div className="max-w-2xl mx-auto p-6 space-y-4" data-testid="psychometric-wizard" dir={language === "ar" ? "rtl" : undefined}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-primary">
                    <BrainCircuit className="w-4 h-4" /> Behavioural baseline
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <ReadingLevelToggle value={level} onChange={setLevel} />
                    <QuestionnaireLanguageToggle value={language} onChange={setLanguage} />
                </div>
            </div>
            <h1 className="text-2xl font-bold">
                Trait {step + 1} of {total}
            </h1>
            <div className="h-1 bg-muted rounded overflow-hidden">
                <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${((step + (canNext ? 1 : 0)) / total) * 100}%` }}
                />
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
                <div className="text-xs uppercase text-muted-foreground mb-1">
                    {q.group_label}
                </div>
                <div className="text-lg font-semibold mb-4" data-testid={`psychometric-prompt-${q.id}`}>
                    {q.prompt}
                </div>
                <div className="flex justify-between gap-2">
                    {LIKERT_TICKS.map((v) => (
                        <button
                            key={v}
                            type="button"
                            onClick={() => {
                                setAnswers((a) => ({ ...a, [q.id]: v }));
                                // Baseline auto-save hint — tiny toast so the user
                                // trusts the "resume any time" promise before they
                                // navigate away. `id` collapses repeat clicks into
                                // a single visible toast; localised across 6 langs.
                                const code = (language || "en").toString().toUpperCase();
                                const msg = DRAFT_SAVED_HINT[code] || DRAFT_SAVED_HINT.EN;
                                toast.success(msg, { id: "psychometric-draft-saved", duration: 1500 });
                            }}
                            className={
                                "flex-1 rounded border py-3 text-sm transition-colors " +
                                (answers[q.id] === v
                                    ? "border-primary bg-primary/10 text-primary font-semibold"
                                    : "border-border hover:bg-muted")
                            }
                            data-testid={`psychometric-likert-${q.id}-${v}`}
                        >
                            {v}
                        </button>
                    ))}
                </div>
                <div className="flex justify-between text-[11px] text-muted-foreground mt-2">
                    <span>{data.likert_scale[0].label}</span>
                    <span>{data.likert_scale[4].label}</span>
                </div>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    disabled={step === 0}
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    data-testid="psychometric-back"
                >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                {step < total - 1 ? (
                    <Button
                        disabled={!canNext}
                        onClick={() => setStep((s) => s + 1)}
                        data-testid="psychometric-next"
                    >
                        Next <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                ) : (
                    <Button
                        disabled={!canNext || busy || answeredCount !== total}
                        onClick={submit}
                        data-testid="psychometric-submit"
                    >
                        {busy ? "Saving…" : "Save baseline"}
                    </Button>
                )}
            </div>
        </div>
    );
}
